import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {forkJoin, of, ReplaySubject} from 'rxjs';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {Ofwca} from '../../interface/ofwca';
import {ListResponse} from '../../interface/response/list-response';
import {OfwcaService} from '../../services/ofwca.service';
import {AffiliateCode} from '../../interface/affiliateCode';
import {PartnerFacility} from '../../interface/partner-facility';

@Component({
  selector: 'app-affiliate-code-dobry-agent-select',
  templateUrl: './affiliate-code-select.component.html',
  styleUrls: ['./affiliate-code-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AffiliateCodeSelectComponent),
      multi: true,
    },
  ],
})
export class AffiliateCodeSelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Kod zniżkowy Chronie.pl';
  @Input() placeholder = 'Wybierz kod zniżkowy';
  @Input() ofwcas: Ofwca[] = [];
  @Input() partnerFacility: PartnerFacility | null = null;
  @Input() required = false;

  partnerItems: AffiliateCode[] = [];
  ofwcaItems: AffiliateCode[] = [];

  value: string | null = null;
  disabled = false;
  loadingOfwca = false;

  private lastSourceKey = '';

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private ofwcaService: OfwcaService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.partnerFacility || changes.ofwcas) {
      this.loadAffiliateCodes();
    }
  }

  writeValue(value: string | null): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  changeValue(value: string | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  compareByCode(a: string | AffiliateCode, b: string | AffiliateCode): boolean {
    const first = typeof a === 'object' ? a?.code : a;
    const second = typeof b === 'object' ? b?.code : b;

    return first === second;
  }

  getOptionLabel(item: AffiliateCode): string {
    const partnerName = item.partner?.fullName
      ?? item.partner?.shortName
      ?? '';

    const ofwcaName = item.ofwca?.fullName ?? '';

    return [partnerName, ofwcaName]
      .filter(Boolean)
      .join(' — ');
  }

  private loadAffiliateCodes(): void {
    const partnerUuids = (this.partnerFacility?.partners ?? [])
      .map((partner: any) => partner?.uuid)
      .filter(Boolean);

    const ofwcaUuids = (this.ofwcas ?? [])
      .map((ofwca: any) => ofwca?.uuid)
      .filter(Boolean);

    const currentKey = [
      `partners:${[...partnerUuids].sort().join('|')}`,
      `ofwcas:${[...ofwcaUuids].sort().join('|')}`,
      `codes:${this.getPartnerCodeKey()}`,
    ].join('::');

    if (currentKey === this.lastSourceKey) {
      return;
    }

    this.lastSourceKey = currentKey;

    // Partnerzy są źródłem podstawowym.
    this.partnerItems = this.sortAffiliateCodes(
      this.uniqueAffiliateCodes(this.getPartnerAffiliateCodes())
        .filter((item) => !item.deleted)
    );

    // OFWCA zostają źródłem dodatkowym.
    this.ofwcaItems = [];

    if (!ofwcaUuids.length) {
      this.loadingOfwca = false;
      return;
    }

    this.loadingOfwca = true;

    const requests = ofwcaUuids.map((ofwcaUuid: string) => {
      return this.ofwcaService.getAffiliateCode(ofwcaUuid)
        .pipe(
          map((response: ListResponse<AffiliateCode>) => response.items ?? []),
          catchError(() => of([] as AffiliateCode[])),
        );
    });

    forkJoin(requests)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((responses: AffiliateCode[][]) => {
        const flattened = responses.reduce((items, current) => {
          return [...items, ...current];
        }, [] as AffiliateCode[]);

        const partnerCodes = new Set(
          this.partnerItems
            .map((item) => item.code)
            .filter(Boolean)
        );

        this.ofwcaItems = this.sortAffiliateCodes(
          this.uniqueAffiliateCodes(flattened)
            .filter((item) => !item.deleted)
            .filter((item) => !partnerCodes.has(item.code))
        );

        this.loadingOfwca = false;
      });
  }

  /**
   * Podstawowe kody pobieramy z partnerów przypisanych do placówki.
   * Obsługujemy zarówno listę affiliateCodes (jeżeli jest dostępna),
   * jak i dotychczasowy mainAffiliateCode.
   */
  private getPartnerAffiliateCodes(): AffiliateCode[] {
    const result: AffiliateCode[] = [];

    (this.partnerFacility?.partners ?? []).forEach((partner: any) => {
      const affiliateCodes = Array.isArray(partner?.affiliateCodes)
        ? partner.affiliateCodes
        : [];

      affiliateCodes.forEach((affiliateCode: any) => {
        if (!affiliateCode?.code) {
          return;
        }

        result.push({
          ...affiliateCode,
          partner: affiliateCode.partner ?? partner,
        } as AffiliateCode);
      });

      const mainAffiliateCode = partner?.mainAffiliateCode;

      if (!mainAffiliateCode) {
        return;
      }

      const code = typeof mainAffiliateCode === 'string'
        ? mainAffiliateCode
        : mainAffiliateCode?.code;

      if (!code) {
        return;
      }

      result.push({
        ...(typeof mainAffiliateCode === 'object' ? mainAffiliateCode : {}),
        code,
        main: true,
        deleted: false,
        partner,
      } as AffiliateCode);
    });

    return result;
  }

  private getPartnerCodeKey(): string {
    return this.getPartnerAffiliateCodes()
      .map((item) => `${item.code}:${item.main ? '1' : '0'}`)
      .sort()
      .join('|');
  }

  private sortAffiliateCodes(items: AffiliateCode[]): AffiliateCode[] {
    return [...items].sort((a, b) => {
      if (a.main !== b.main) {
        return a.main ? -1 : 1;
      }

      return (a.code ?? '').localeCompare(b.code ?? '');
    });
  }

  private uniqueAffiliateCodes(items: AffiliateCode[]): AffiliateCode[] {
    const mapByCode = new Map<string, AffiliateCode>();

    items.forEach((item) => {
      if (!item?.code) {
        return;
      }

      if (!mapByCode.has(item.code)) {
        mapByCode.set(item.code, item);
        return;
      }

      const existing = mapByCode.get(item.code);

      if (!existing?.main && item.main) {
        mapByCode.set(item.code, item);
      }
    });

    return Array.from(mapByCode.values());
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
